/** Render-only local-body presentation. The selected avatar's animator/SSX owns
 * the rig before and after this synchronous draw; no mixer or pose is retained.
 * Controller grip positions/quaternions are WORLD-space in the rendered chart
 * (metres in Valoria, 100 units/metre in SSX), never raw XR-reference poses.
 */
export function createFirstPersonBody({THREE: T}) {
  const masks = new WeakMap(), resources = new Set(), rigs = new WeakMap(), rootRigs = new WeakMap();
  let currentRig = null, walkingBlend = null;
  const finiteVector = p => p && ['x', 'y', 'z'].every(k => Number.isFinite(p[k]) && Math.abs(p[k]) < 1e12);
  const finiteMatrix = m => m?.elements?.every(Number.isFinite);
  const finiteQuaternion = q => q && ['x', 'y', 'z', 'w'].every(k => Number.isFinite(q[k]) && Math.abs(q[k]) < 1e12) && Math.hypot(q.x, q.y, q.z, q.w) > 1e-8;
  const headName = name => /^(?:face|skinface|hair|head|eye|brow|eyelash|helmet)(?:$|[_.\d])/i.test(name ?? '') || /_(?:FACE|EYE)(?:$|[_.])/i.test(name ?? '');
  const position = object => new T.Vector3().setFromMatrixPosition(object.matrixWorld);
  function retireRig(rig) {
    for (const mesh of rig?.meshes ?? []) { const mask = masks.get(mesh); if (mask?.geometry) { mask.geometry.dispose(); resources.delete(mask.geometry); } masks.delete(mesh); }
  }

  function gripCalibration(rig, side) {
    const {hand, lower} = rig.arms[side], bind = rig.bindMatrices;
    if (!hand || !lower || !bind.has(hand) || !bind.has(lower)) return new T.Quaternion();
    const handInverse = bind.get(hand).clone().invert();
    const localPoint = bone => bone && bind.has(bone) ? new T.Vector3().setFromMatrixPosition(bind.get(bone)).applyMatrix4(handInverse) : null;
    const descendants = []; hand.traverse(bone => { if (bone.isBone) descendants.push(bone); });
    const finger = name => descendants.find(bone => new RegExp(`(?:^|_)${name}(?:1|_?0?1)?(?:$|_left$|_right$)`, 'i').test(bone.name));
    const middle = localPoint(finger('Middle')), index = localPoint(finger('Index')), little = localPoint(finger('Little') ?? finger('Pinky'));
    // The actual bind matrices remove ZoeSourceFrame and per-model bone axes.
    // Finger roots give the authored palm plane where the rig provides them.
    const forward = middle ?? localPoint(lower)?.negate();
    let thumb = index && little ? index.sub(little) : null;
    if (!thumb && rig.arms.left.hand && rig.arms.right.hand && rig.hips && rig.neck) {
      const left = localPoint(rig.arms.left.hand), right = localPoint(rig.arms.right.hand), neck = localPoint(rig.neck), hips = localPoint(rig.hips);
      // Fingerless rigs: anatomical LEFT crossed with UP points to the front,
      // which is the thumb side of the authored open hand. Reversing left/right
      // leaves the fingers unchanged but rolls each palm 180 degrees backward.
      // Use bind points so ZoeSourceFrame's Y/Z-up conversion cancels out.
      if (left && right && neck && hips) thumb = left.sub(right).cross(neck.sub(hips));
    }
    if (!forward || !thumb || forward.lengthSq() < 1e-12 || thumb.lengthSq() < 1e-12) return new T.Quaternion();
    const y = forward.normalize().negate(), z = thumb.normalize().negate(); z.addScaledVector(y, -z.dot(y));
    if (z.lengthSq() < 1e-12) return new T.Quaternion();
    z.normalize(); const x = new T.Vector3().crossVectors(y, z).normalize(); z.crossVectors(x, y).normalize();
    // WebXR grip: -Z runs toward the thumb and ±X is the back of each hand.
    // https://www.w3.org/TR/webxr/#dom-xrinputsource-gripspace
    return new T.Quaternion().setFromRotationMatrix(new T.Matrix4().makeBasis(x, y, z)).invert().normalize();
  }

  function getRig(root, body, preset) {
    let rig = rigs.get(body);
    const revision = root.userData?.avatarModelRevision;
    if (rig && rig.revision === revision && rig.preset === preset && rig.children.length === body.children.length && rig.children.every((node, i) => node === body.children[i])) return rig;
    const previous = rig ?? rootRigs.get(root), nodes = [], names = new Map(), meshes = [], skeletons = new Set();
    body.traverse(node => { nodes.push(node); if (node.isBone) names.set(node.name.toLowerCase(), node); if (node.isMesh) { meshes.push(node); if (node.skeleton) skeletons.add(node.skeleton); } });
    const bone = (...keys) => keys.map(key => names.get(key.toLowerCase())).find(Boolean);
    const head = bone('J_Bip_C_Head', 'head'), headBones = new Set();
    head?.traverse(node => { if (node.isBone) headBones.add(node); });
    // Source-exported head attachments may be siblings of the head. Do not
    // classify all secondary bones: clothing must remain visible.
    for (const node of nodes) if (node.isBone && (/^sec_mop_/i.test(node.name) || node.userData?.ssxHeadAttachment === true)) headBones.add(node);
    const arms = {};
    for (const side of ['left', 'right']) {
      const letter = side === 'left' ? 'L' : 'R';
      arms[side] = {upper: bone(`J_Bip_${letter}_UpperArm`, `bicep${side}`, `UpperArm_${letter}`), lower: bone(`J_Bip_${letter}_LowerArm`, `forearm${side}`, `LowerArm_${letter}`), hand: bone(`J_Bip_${letter}_Hand`, `hand${side}`, `Hand_${letter}`)};
    }
    // A customization/preset replacement can retire the old meshes while the
    // stable body survives. Do not retain their cloned mask geometry forever.
    for (const mesh of previous?.meshes ?? []) if (!meshes.includes(mesh)) {
      const mask = masks.get(mesh); if (mask?.geometry) { mask.geometry.dispose(); resources.delete(mask.geometry); } masks.delete(mesh);
    }
    const bindMatrices = new Map();
    for (const skeleton of skeletons) skeleton.bones.forEach((bone, i) => { const inverse = skeleton.boneInverses[i]; if (finiteMatrix(inverse) && Math.abs(inverse.determinant()) > 1e-15) bindMatrices.set(bone, inverse.clone().invert()); });
    const torsoBones = new Set([bone('J_Bip_C_Spine', 'lowerspine'), bone('J_Bip_C_Chest', 'middlespine'), bone('J_Bip_C_UpperChest', 'upperspine')].filter(Boolean));
    const torso = nodes.filter(node => torsoBones.has(node)).flatMap(node => {
      // Use actual parent-relative inverse binds, never an animated first frame
      // or a guessed axis. This also cancels Zoe's authored source-frame axes.
      const bind = bindMatrices.get(node), parent = bindMatrices.get(node.parent);
      if (!bind || !parent) return [];
      const local = parent.clone().invert().multiply(bind), rotation = new T.Quaternion();
      local.decompose(new T.Vector3(), rotation, new T.Vector3());
      return finiteQuaternion(rotation) ? [{bone: node, rotation: rotation.normalize()}] : [];
    });
    rig = {revision, preset, children: body.children.slice(), nodes, meshes, skeletons: [...skeletons], bindMatrices, headBones, arms, torso, head, hips: bone('J_Bip_C_Hips', 'hips'), neck: bone('J_Bip_C_Neck', 'neck'), states: null, inUse: false};
    for (const side of ['left', 'right']) {
      arms[side].orientationOffset = gripCalibration(rig, side);
      const nodes = []; arms[side].upper?.traverse(node => nodes.push(node));
      arms[side].rollback = stateBuffers({nodes, skeletons: []});
    }
    rigs.set(body, rig); rootRigs.set(root, rig); return rig;
  }

  function namedHeadMesh(mesh, body, headBones) {
    // Restrict ancestor matching to mesh branches; never hide the rig/root.
    for (let node = mesh; node && node !== body; node = node.parent) {
      if (headBones.has(node) || (!node.isBone && headName(node.name))) return true;
    }
    return false;
  }

  function headMask(mesh, rig, body) {
    const source = mesh.geometry, skeleton = mesh.skeleton, material = mesh.material;
    const cached = masks.get(mesh);
    if ((cached?.source === source || cached?.geometry === source) && cached.skeleton === skeleton && cached.material === material) return cached;
    if (cached?.geometry) { cached.geometry.dispose(); resources.delete(cached.geometry); }
    const result = {source, skeleton, material, hidden: false, geometry: null};
    masks.set(mesh, result);
    if (namedHeadMesh(mesh, body, rig.headBones)) { result.hidden = true; return result; }
    if (!source?.attributes?.position) return result;
    const skinIndex = source.getAttribute('skinIndex'), skinWeight = source.getAttribute('skinWeight');
    const headIndices = new Set(skeleton?.bones?.flatMap((bone, i) => rig.headBones.has(bone) ? [i] : []) ?? []);
    // The current male/Valor jacket has a hood weighted only 10% to its
    // secondary hood joint and 90% to shoulders. It is not a head descendant,
    // yet its upper back can swing through the fixed first-person eye.
    const hoodIndices = new Set(skeleton?.bones?.flatMap((bone, i) => /(?:^|_)hood(?:$|_)/i.test(bone.name) ? [i] : []) ?? []);
    const neckBind = rig.bindMatrices.get(rig.neck), neckHeight = neckBind?.elements[13];
    const materials = Array.isArray(material) ? material : [material];
    const headMaterials = new Set(materials.flatMap((item, i) => headName(item?.name) ? [i] : []));
    if (!headIndices.size && !headMaterials.size && !hoodIndices.size) return result;
    const weights = new Float32Array(source.attributes.position.count), upperHood = new Uint8Array(weights.length), bindPoint = new T.Vector3();
    if (skinIndex && skinWeight && (headIndices.size || hoodIndices.size)) {
      for (let i = 0; i < weights.length; i++) {
        let hoodWeight = 0;
        for (let slot = 0; slot < Math.min(4, skinIndex.itemSize, skinWeight.itemSize); slot++) {
          const joint = skinIndex.getComponent(i, slot), weight = skinWeight.getComponent(i, slot);
          if (headIndices.has(joint)) weights[i] += weight;
          if (hoodIndices.has(joint)) hoodWeight += weight;
        }
        // Preserve the chest, shoulders and every triangle entirely below the
        // authored neck plane, including cloth with minor hood influence.
        if (hoodWeight >= .05 && Number.isFinite(neckHeight) && finiteMatrix(mesh.bindMatrix)) {
          bindPoint.fromBufferAttribute(source.attributes.position, i).applyMatrix4(mesh.bindMatrix);
          upperHood[i] = bindPoint.y >= neckHeight ? 1 : 0;
        }
      }
    }
    const index = source.index, count = index?.count ?? weights.length;
    const groups = source.groups.length ? source.groups : [{start: 0, count, materialIndex: 0}];
    const selected = [], selectedGroups = [];
    const rangeStart = Math.max(0, source.drawRange.start), rangeEnd = Math.min(count, rangeStart + source.drawRange.count);
    let removed = 0;
    for (const group of groups) {
      const start = selected.length, end = Math.min(count, group.start + group.count, rangeEnd);
      for (let i = Math.max(group.start, rangeStart); i + 2 < end; i += 3) {
        const a = index ? index.getX(i) : i, b = index ? index.getX(i + 1) : i + 1, c = index ? index.getX(i + 2) : i + 2;
        // Head-dominant triangles are removed from mixed body meshes, while
        // neck/torso/hand triangles keep their original attributes and shader.
        if (upperHood[a] || upperHood[b] || upperHood[c] || headMaterials.has(group.materialIndex) || Math.max(weights[a], weights[b], weights[c]) >= .5 || (weights[a] + weights[b] + weights[c]) / 3 >= .35) { removed++; continue; }
        selected.push(a, b, c);
      }
      if (selected.length > start) selectedGroups.push({start, count: selected.length - start, materialIndex: group.materialIndex});
    }
    if (!removed) return result;
    if (!selected.length) { result.hidden = true; return result; }
    // Own the filtered geometry/index. Source vertices, materials, skeleton and
    // morph data remain untouched, including those shared by remote avatars.
    const geometry = source.clone(); geometry.setIndex(selected); geometry.clearGroups();
    for (const group of selectedGroups) geometry.addGroup(group.start, group.count, group.materialIndex);
    geometry.setDrawRange(0, selected.length); result.geometry = geometry; resources.add(geometry); return result;
  }

  function stateBuffers(rig) {
    return {nodes: rig.nodes.map(node => ({node, matrix: new T.Matrix4(), world: new T.Matrix4(), bind: node.isSkinnedMesh ? new T.Matrix4() : null})), skeletons: rig.skeletons.map(skeleton => ({skeleton, matrices: skeleton.boneMatrices?.slice()}))};
  }
  function capture(states) {
    for (const state of states.nodes) {
      const n = state.node;
      state.matrix.copy(n.matrix); state.world.copy(n.matrixWorld); state.auto = n.matrixAutoUpdate; state.worldAuto = n.matrixWorldAutoUpdate;
      state.dirty = n.matrixWorldNeedsUpdate; state.visible = n.visible; state.geometry = n.geometry; state.culled = n.frustumCulled;
      if (state.bind) state.bind.copy(n.bindMatrixInverse);
    }
    for (const state of states.skeletons) {
      const matrices = state.skeleton.boneMatrices;
      // The first WebGL draw replaces this CPU array with texture-aligned
      // storage. Fresh avatar switches often get that first draw inside this
      // scope, so a fixed-size snapshot would throw on the following frame.
      if (state.matrices?.length !== matrices?.length) state.matrices = matrices?.slice();
      else if (matrices) state.matrices.set(matrices);
    }
  }
  function restore(states) {
    for (const state of states.nodes) {
      const n = state.node;
      n.matrix.copy(state.matrix); n.matrixWorld.copy(state.world); n.matrixAutoUpdate = state.auto; n.matrixWorldAutoUpdate = state.worldAuto;
      n.matrixWorldNeedsUpdate = state.dirty; n.visible = state.visible; n.frustumCulled = state.culled;
      if (state.geometry) n.geometry = state.geometry;
      if (state.bind) n.bindMatrixInverse.copy(state.bind);
    }
    for (const {skeleton, matrices} of states.skeletons) if (matrices) { skeleton.boneMatrices.set(matrices); if (skeleton.boneTexture) skeleton.boneTexture.needsUpdate = true; }
  }

  function writeWorld(bone, world) {
    if (!bone.parent || !finiteMatrix(world) || !finiteMatrix(bone.parent.matrixWorld) || Math.abs(bone.parent.matrixWorld.determinant()) < 1e-15) return false;
    const local = bone.parent.matrixWorld.clone().invert().multiply(world);
    if (!finiteMatrix(local)) return false;
    bone.matrix.copy(local); bone.matrixAutoUpdate = false; bone.matrixWorldNeedsUpdate = true;
    // matrixWorldAutoUpdate can also be disabled by native pose ownership.
    const update = node => { if (node !== bone && node.matrixAutoUpdate) node.updateMatrix(); node.matrixWorld.multiplyMatrices(node.parent.matrixWorld, node.matrix); node.matrixWorldNeedsUpdate = false; for (const child of node.children) update(child); };
    update(bone); return true;
  }
  function rotateAt(bone, quaternion, pivot) {
    const delta = new T.Matrix4().makeTranslation(pivot.x, pivot.y, pivot.z).multiply(new T.Matrix4().makeRotationFromQuaternion(quaternion)).multiply(new T.Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z));
    return writeWorld(bone, delta.multiply(bone.matrixWorld));
  }
  function walkingWeight(locomotion, root, rig) {
    if (!locomotion) { walkingBlend = null; return 0; }
    const target = locomotion.walking === true && /^(Walk|Jog|Run)$/.test(locomotion.animationState) ? 1 : 0;
    const time = locomotion.time, owner = root.userData?.portalContinuationSourceUuid ?? root.uuid;
    // Only a scalar ease is retained, never a source pose, mixer or gait phase.
    // A source and its portal continuation share the same frame/owner so eye or
    // repeated main draws cannot accelerate the ease or restart it mid-stride.
    if (locomotion.riding === true) { walkingBlend = null; return 0; }
    if (!Number.isFinite(time)) { walkingBlend = null; return target; }
    if (!walkingBlend || walkingBlend.owner !== owner || walkingBlend.preset !== rig.preset || walkingBlend.revision !== rig.revision || time < walkingBlend.time || time - walkingBlend.time > .25) {
      walkingBlend = {owner, preset: rig.preset, revision: rig.revision, time, weight: 0};
    } else if (time > walkingBlend.time) {
      walkingBlend.weight += (target - walkingBlend.weight) * (1 - Math.exp(-(time - walkingBlend.time) / .12));
      walkingBlend.time = time;
    }
    return walkingBlend.weight;
  }
  function softenWalkingTorso(rig, weight) {
    if (!(weight > 1e-6)) return;
    for (const {bone, rotation: bindRotation} of rig.torso) {
      if (!finiteMatrix(bone.matrix) || Math.abs(bone.matrix.determinant()) < 1e-15) continue;
      const translation = new T.Vector3(), rotation = new T.Quaternion();
      bone.matrix.decompose(translation, rotation, new T.Vector3());
      if (!finiteQuaternion(rotation)) continue;
      const correction = rotation.clone().slerp(bindRotation, .55 * weight).multiply(rotation.clone().invert());
      // Retain the local translation, scale and any native portal-pose residual.
      // Hips and leg transforms are never changed: reducing their vertical bob
      // alone would float the feet. Arms keep their authored pose (the bind pose
      // can be a T-pose), but inherit the calmer chest before controller IK.
      const local = new T.Matrix4().makeRotationFromQuaternion(correction).multiply(bone.matrix).setPosition(translation);
      writeWorld(bone, bone.parent.matrixWorld.clone().multiply(local));
    }
  }
  function solveArm(rig, side, pose, root, unitsPerMeter) {
    const {upper, lower, hand} = rig.arms[side];
    const below = (node, ancestor) => { for (let parent = node?.parent; parent; parent = parent.parent) if (parent === ancestor) return true; return false; };
    if (pose?.tracked === false || !finiteVector(pose?.position) || !finiteQuaternion(pose?.quaternion) || !upper || !lower || !hand || !below(lower, upper) || !below(hand, lower)) return false;
    if (![upper, lower, hand].every(bone => finiteMatrix(bone.matrixWorld) && Math.abs(bone.matrixWorld.determinant()) > 1e-15 && bone.parent && Math.abs(bone.parent.matrixWorld.determinant()) > 1e-15)) return false;
    const shoulder = position(upper), elbow = position(lower), wrist = position(hand);
    const a = elbow.distanceTo(shoulder), b = wrist.distanceTo(elbow), total = a + b;
    const epsilon = Math.max(total * 1e-5, unitsPerMeter * 1e-7);
    if (![a, b, total].every(Number.isFinite) || Math.min(a, b) < epsilon * 4) return false;
    const target = new T.Vector3(pose.position.x, pose.position.y, pose.position.z), direction = target.clone().sub(shoulder);
    const requestedDistance = direction.length();
    if (!Number.isFinite(requestedDistance)) return false;
    if (requestedDistance < epsilon) direction.copy(wrist).sub(shoulder);
    if (direction.lengthSq() < epsilon * epsilon) direction.copy(elbow).sub(shoulder);
    direction.normalize();
    const distance = Math.max(Math.abs(a - b) + epsilon, Math.min(total - epsilon, requestedDistance));
    target.copy(shoulder).addScaledVector(direction, distance);
    const opposite = rig.arms[side === 'left' ? 'right' : 'left'].upper;
    const outward = opposite ? shoulder.clone().sub(position(opposite)) : new T.Vector3(side === 'left' ? -1 : 1, 0, 0).transformDirection(root.matrixWorld);
    const up = rig.neck && rig.hips ? position(rig.neck).sub(position(rig.hips)).normalize() : new T.Vector3(0, 1, 0).transformDirection(root.matrixWorld);
    const preferred = outward.normalize().addScaledVector(up, -.8);
    preferred.addScaledVector(direction, -preferred.dot(direction));
    const pole = elbow.clone().sub(shoulder); pole.addScaledVector(direction, -pole.dot(direction));
    // The current animation chooses a natural bend where possible; the torso
    // pole prevents the collinear, fully extended arm from flipping or NaN-ing.
    if (pole.lengthSq() < total * total * .0025) pole.copy(preferred);
    if (pole.lengthSq() < epsilon * epsilon) { pole.set(0, 0, 1); pole.addScaledVector(direction, -pole.dot(direction)); }
    if (pole.lengthSq() < epsilon * epsilon) { pole.set(1, 0, 0); pole.addScaledVector(direction, -pole.dot(direction)); }
    pole.normalize();
    const along = (a * a - b * b + distance * distance) / (2 * distance), height = Math.sqrt(Math.max(0, a * a - along * along));
    const desiredElbow = shoulder.clone().addScaledVector(direction, along).addScaledVector(pole, height);
    const upperDelta = new T.Quaternion().setFromUnitVectors(elbow.clone().sub(shoulder).normalize(), desiredElbow.clone().sub(shoulder).normalize());
    if (!rotateAt(upper, upperDelta, shoulder)) return false;
    const nextElbow = position(lower), nextWrist = position(hand);
    const lowerDelta = new T.Quaternion().setFromUnitVectors(nextWrist.sub(nextElbow).normalize(), target.clone().sub(nextElbow).normalize());
    if (!rotateAt(lower, lowerDelta, nextElbow)) return false;
    const currentOrientation = new T.Quaternion(); hand.matrixWorld.decompose(new T.Vector3(), currentOrientation, new T.Vector3());
    const desiredOrientation = new T.Quaternion(pose.quaternion.x, pose.quaternion.y, pose.quaternion.z, pose.quaternion.w).normalize();
    // Optional per-avatar grip calibration, in hand-local coordinates.
    if (finiteQuaternion(pose.orientationOffset)) desiredOrientation.multiply(new T.Quaternion(pose.orientationOffset.x, pose.orientationOffset.y, pose.orientationOffset.z, pose.orientationOffset.w).normalize());
    else desiredOrientation.multiply(rig.arms[side].orientationOffset);
    const orientationDelta = desiredOrientation.multiply(currentOrientation.normalize().invert());
    return rotateAt(hand, orientationDelta, position(hand));
  }

  function begin({avatarRoot, avatarBody = avatarRoot, preset, controllerPoses, unitsPerMeter = 1, locomotion} = {}) {
    if (!avatarRoot?.isObject3D || !avatarBody?.isObject3D) return () => {};
    const rig = getRig(avatarRoot, avatarBody, preset ?? avatarBody.userData?.avatarPreset);
    // Only the main-view local body needs a mask. Portal ghost/source swaps can
    // replace both roots and bodies, so bound resources across those changes as
    // well as ordinary model revisions. Never retire an active nested draw.
    const previousRig = currentRig;
    if (previousRig && previousRig !== rig && !previousRig.inUse) retireRig(previousRig);
    currentRig = rig;
    const nested = rig.inUse, states = nested ? stateBuffers(rig) : (rig.states ??= stateBuffers(rig));
    capture(states); rig.inUse = true;
    let ended = false;
    const cleanup = () => {
      if (ended) return; ended = true; restore(states); rig.inUse = nested;
      if (previousRig && previousRig !== rig && previousRig.inUse) { currentRig = previousRig; if (!rig.inUse) retireRig(rig); }
    };
    try {
      for (const mesh of rig.meshes) {
        const mask = headMask(mesh, rig, avatarBody);
        if (mask.hidden) mesh.visible = false; else if (mask.geometry) mesh.geometry = mask.geometry;
        if (mesh.isSkinnedMesh) mesh.frustumCulled = false;
      }
      const weight = nested ? 0 : walkingWeight(locomotion, avatarRoot, rig);
      if (weight > 1e-6 || controllerPoses?.left || controllerPoses?.right) {
        // Read current post-animation matrices without touching parent charts.
        avatarBody.updateMatrixWorld(true);
        softenWalkingTorso(rig, weight);
        const units = Number.isFinite(unitsPerMeter) && unitsPerMeter > 0 ? unitsPerMeter : 1;
        for (const side of ['left', 'right']) {
          const pose = controllerPoses?.[side];
          if (!pose) continue;
          // Restore an invalid partial solve to this softened frame, not the
          // undamped source-world arm under a different chest orientation.
          const armState = rig.arms[side].rollback;
          capture(armState);
          if (solveArm(rig, side, pose, avatarRoot, units)) continue;
          // If validation fails after a proximal rotation, roll back that arm
          // atomically. The other tracked hand remains independent.
          restore(armState);
        }
        for (const skeleton of rig.skeletons) skeleton.update();
      }
      return cleanup;
    } catch (error) { cleanup(); throw error; }
  }
  return {
    begin,
    withFrame(options, draw) { const cleanup = begin(options); try { return draw(); } finally { cleanup(); } },
    dispose() { for (const geometry of resources) geometry.dispose(); resources.clear(); },
  };
}
